import { useState } from "react";
import {
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Drawer,
  Typography,
  IconButton,
  Box,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  BaseSearchFilters,
  SearchFilters,
  SearchState,
  SORT_OPTIONS,
} from "../../interfaces/Search";
import { FilterConfig } from "./config";
import { useSelector } from "react-redux";

interface SearchAndFiltersProps<T> {
  onSearch: (params: any, metadata?: any) => void;
  totalShown: number;
  pagination: SearchState<T>["pagination"];
  filters: SearchFilters;
  sorting: SearchState<T>["sorting"];
  filterConfig: FilterConfig; // Define what filters to show
  isLoading?: boolean;
}

const SearchAndFilters = <T,>({
  onSearch,
  totalShown,
  pagination,
  filters,
  sorting,
  filterConfig,
  isLoading = false,
}: SearchAndFiltersProps<T>) => {
  const reduxUser: any = useSelector((state: any) => state.user);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState<BaseSearchFilters | any>(
    filters
  );
  const [currentSort, setCurrentSort] = useState(sorting);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const handKeyDown = (event: Event | any) => {
    if (event.key === "Enter") {
      onSearch(
        {
          query: searchQuery,
          pagination: {
            ...pagination,
            currentPage: 1, // Reset to first page on new search
          },
          filters: activeFilters,
          sorting: currentSort,
        },
        { userId: reduxUser.id }
      );
    }
  };

  const handleSearchChange = (event: Event | any) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    onSearch(
      {
        query: searchQuery,
        pagination: {
          ...pagination,
          currentPage: newPage,
        },
        filters: activeFilters,
        sorting: currentSort,
      },
      { userId: reduxUser.id }
    );
  };

  const handleFilterChange = (newFilters: any) => {
    setActiveFilters(newFilters);
    onSearch(
      {
        query: searchQuery,
        pagination: {
          ...pagination,
          currentPage: 1, // Reset to first page on filter change
        },
        filters: newFilters,
        sorting: currentSort,
      },
      { userId: reduxUser.id }
    );
  };

  const handleSortChange = (event: Event | any) => {
    try {
      const value = event.target.value;
      const lastChar = value.slice(-1);
      const sortOrder = lastChar === "1" ? -1 : 1;
      const sortBy = value.slice(0, value.lastIndexOf("-"));

      const newSort = { sortBy, sortOrder };
      setCurrentSort(newSort);

      onSearch(
        {
          query: searchQuery,
          pagination: {
            ...pagination,
            currentPage: currentPage,
          },
          filters: activeFilters,
          sorting: newSort,
        },
        { userId: reduxUser.id }
      );
    } catch (error) {
      console.error("Sort handling error:", error);
      const defaultSort = { sortBy: "createdAt", sortOrder: -1 };
      setCurrentSort(defaultSort);

      onSearch(
        {
          query: searchQuery,
          pagination: {
            ...pagination,
            currentPage: currentPage,
          },
          filters: activeFilters,
          sorting: defaultSort,
        },
        { userId: reduxUser.id }
      );
    }
  };

  return (
    <div>
      {/* Search and Filter Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
          opacity: isLoading ? 0.7 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        <TextField
          fullWidth
          placeholder="Press enter to search..."
          value={searchQuery}
          onKeyDown={handKeyDown}
          onChange={handleSearchChange}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  style={{
                    color: isLoading ? "text.disabled" : "text.secondary",
                    width: 20,
                    height: 20,
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={`${currentSort.sortBy}${
                currentSort.sortOrder === 1 ? "-2" : "-1"
              }`}
              onChange={handleSortChange}
              label="Sort by"
              disabled={isLoading}
            >
              {Object.values(SORT_OPTIONS).map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={() => setIsFilterDrawerOpen(true)}
            sx={{ minWidth: "auto", p: "6px" }}
            disabled={isLoading}
          >
            <SlidersHorizontal size={20} />
          </Button>
        </Box>
      </Box>

      {/* Active Filters */}
      {Object.keys(activeFilters).length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          {filterConfig.available.map((filterKey) => {
            const value = activeFilters[filterKey];
            const filterOption = filterConfig.options[filterKey];

            if (!value) return null;

            // Handle different filter types
            let displayValue = "";
            switch (filterOption.type) {
              case "select":
                const option = filterOption.selectOptions?.find(
                  (opt) => opt.value === value
                );
                displayValue = option?.label || value;
                break;

              case "range":
                if (
                  filterKey === "priceRange" &&
                  value.min > 0 &&
                  value.max > 0
                ) {
                  displayValue = `$${value.min} - $${value.max}`;
                }
                break;

              case "date":
                // Format date for display
                const date = new Date(value);
                displayValue = date.toLocaleDateString();
                break;

              default:
                displayValue = value;
            }

            if (!displayValue) return null;

            return (
              <Chip
                key={filterKey}
                label={`${filterOption.label}: ${displayValue}`}
                onDelete={() => {
                  if (filterKey === "priceRange") {
                    handleFilterChange({
                      ...activeFilters,
                      [filterKey]: { min: 0, max: 0 },
                    });
                  } else {
                    handleFilterChange({
                      ...activeFilters,
                      [filterKey]: null,
                    });
                  }
                }}
                size="small"
              />
            );
          })}
        </Box>
      )}

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={() => !isLoading && setIsFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 320, p: 3 }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setIsFilterDrawerOpen(false)}>
              <X size={20} />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {filterConfig.available.map((filterKey) => {
              const filterOption = filterConfig.options[filterKey];

              switch (filterOption.type) {
                case "select":
                  return (
                    <FormControl key={filterKey} fullWidth>
                      <InputLabel>{filterOption.label}</InputLabel>
                      <Select
                        value={activeFilters[filterKey] || ""}
                        onChange={(e) =>
                          handleFilterChange({
                            ...activeFilters,
                            [filterKey]: e.target.value,
                          })
                        }
                        label={filterOption.label}
                      >
                        {filterOption.selectOptions?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );

                case "text":
                  return (
                    <TextField
                      key={filterKey}
                      fullWidth
                      label={filterOption.label}
                      placeholder={`Enter ${filterOption.label.toLowerCase()}...`}
                      value={activeFilters[filterKey] || ""}
                      onChange={(e) =>
                        handleFilterChange({
                          ...activeFilters,
                          [filterKey]: e.target.value,
                        })
                      }
                    />
                  );

                case "range":
                  return (
                    <Box key={filterKey}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {filterOption.label}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField
                          type="number"
                          placeholder="Min"
                          value={activeFilters.priceRange?.min || ""}
                          onChange={(e) =>
                            handleFilterChange({
                              ...activeFilters,
                              priceRange: {
                                ...activeFilters.priceRange,
                                min: parseInt(e.target.value),
                              },
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          type="number"
                          placeholder="Max"
                          value={activeFilters.priceRange?.max || ""}
                          onChange={(e) =>
                            handleFilterChange({
                              ...activeFilters,
                              priceRange: {
                                ...activeFilters.priceRange,
                                max: parseInt(e.target.value),
                              },
                            })
                          }
                          fullWidth
                        />
                      </Box>
                    </Box>
                  );

                case "date":
                  return (
                    <TextField
                      key={filterKey}
                      type="date"
                      label={filterOption.label}
                      InputLabelProps={{ shrink: true }}
                      value={activeFilters[filterKey] || ""}
                      onChange={(e) =>
                        handleFilterChange({
                          ...activeFilters,
                          [filterKey]: e.target.value,
                        })
                      }
                      fullWidth
                    />
                  );

                default:
                  return null;
              }
            })}
          </Box>
        </Box>
      </Drawer>

      {/* Pagination */}
      {pagination && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {totalShown} to{" "}
            {Math.min(
              pagination.currentPage * pagination.limit,
              pagination.total
            )}{" "}
            of {pagination.total} results
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage || isLoading}
              startIcon={<ChevronLeft size={16} />}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage || isLoading}
              endIcon={<ChevronRight size={16} />}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default SearchAndFilters;
